import { api, requireAccount, withAccount } from "./core";

// ── Developer tools (non-production only) ────────────────────────────────────
// The backend gates /api/v1/dev/* behind a double env lock and only registers
// the routes off-prod (404 otherwise). context() returning dev_tools:true is
// what the DevMenu uses to decide whether to render at all — the FE check is
// UX; the BE is the real boundary.
export interface DevContext {
  environment: string;
  dev_tools: boolean;
  account_id: number;
  account_login?: string;
  account_type?: string; // "personal" | "org" — gates which plans apply
  plan?: string;
  membership_role?: string;
  billable?: boolean; // does the caller occupy a paid Nova seat (membership.billable)
  user?: { id: number; login: string; is_staff: boolean; ex_github_id: number };
}
export interface DevUser {
  login: string;
  ex_github_id: number;
  membership_role: string;
}

export const devApi = {
  context: (accountLogin: string) =>
    api.get<DevContext>(withAccount("/dev/context", requireAccount(accountLogin, "devApi.context"))),
  users: (accountLogin: string) =>
    api.get<{ users: DevUser[] }>(withAccount("/dev/users", requireAccount(accountLogin, "devApi.users"))),
  setMembershipRole: (accountLogin: string, role: string) =>
    api.post<{ membership_role: string }>(
      withAccount("/dev/membership-role", requireAccount(accountLogin, "devApi.setMembershipRole")),
      { role },
    ),
  setSeat: (accountLogin: string, billable: boolean) =>
    api.post<{ billable: boolean }>(
      withAccount("/dev/seat", requireAccount(accountLogin, "devApi.setSeat")),
      { billable },
    ),
  setPlan: (accountLogin: string, plan: string) =>
    api.post<{ plan: string }>(withAccount("/dev/plan", requireAccount(accountLogin, "devApi.setPlan")), { plan }),
  // Write a synthetic subscription (plan + status + seats) so the FULL billing
  // lifecycle can be exercised without Stripe: account.plan follows the
  // entitlement projection (past_due/canceled → free) and a downgrade clears
  // seats, so the lapsed-Nova banner + free-limit fallout reproduce.
  setSubscriptionState: (accountLogin: string, body: { plan: string; status: string; seats: number }) =>
    api.post<{ plan: string; status: string; seats: number }>(
      withAccount("/dev/subscription-state", requireAccount(accountLogin, "devApi.setSubscriptionState")),
      body,
    ),
  setUserVisibility: (accountLogin: string, exGithubId: number, visibility: string) =>
    api.post<{ ex_github_id: number; visibility: string }>(
      withAccount("/dev/user-visibility", requireAccount(accountLogin, "devApi.setUserVisibility")),
      { ex_github_id: exGithubId, visibility },
    ),
  setDisclosure: (accountLogin: string, level: string, exGithubId?: number) =>
    api.post<{ ex_github_id: number; level: string }>(
      withAccount("/dev/disclosure", requireAccount(accountLogin, "devApi.setDisclosure")),
      { level, ex_github_id: exGithubId ?? 0 },
    ),
  // Turn the caller's membership in the active galaxy into an archived
  // (departed) affiliation, or restore it. Optional joined_at/departed_at
  // backfill the tenure window (YYYY-MM-DD) for certificate/affiliation debug.
  setAffiliation: (
    accountLogin: string,
    body: { action: "archive" | "restore"; joinedAt?: string; departedAt?: string },
  ) =>
    api.post<{ created_at: string; departed_at: string | null; billable: boolean; is_active: boolean }>(
      withAccount("/dev/affiliation", requireAccount(accountLogin, "devApi.setAffiliation")),
      { action: body.action, joined_at: body.joinedAt ?? "", departed_at: body.departedAt ?? "" },
    ),
  // Delete the caller's Gravity Certificate(s) in the active galaxy so a re-mint
  // starts clean.
  clearCertificate: (accountLogin: string) =>
    api.post<{ cleared: boolean }>(
      withAccount("/dev/certificate", requireAccount(accountLogin, "devApi.clearCertificate")),
      { action: "clear" },
    ),
  // Revive the active galaxy's per-day manual-observation quota by deleting its
  // quota-counting observation_log rows in the trailing 24h window (used → 0).
  // dev-only; the endpoint 404s in prod.
  resetObservationQuota: (accountLogin: string) =>
    api.post<{ reset: boolean }>(
      withAccount("/dev/observation-quota", requireAccount(accountLogin, "devApi.resetObservationQuota")),
      {},
    ),
  // Flip the active galaxy's onboarding_completed back to false so the first-run
  // flow re-shows on reload (Layout gates Onboarding on onboardingApi.status).
  // dev-only; the endpoint 404s in prod.
  resetOnboarding: (accountLogin: string) =>
    api.post<{ reset: boolean }>(
      withAccount("/dev/onboarding", requireAccount(accountLogin, "devApi.resetOnboarding")),
      {},
    ),
  // Physically delete ALL observation data for the active galaxy so the
  // never-observed / first-observation flow can be re-run from a clean slate.
  // Returns the per-table deleted-row counts. This is the deliberate D-07
  // exception — dev-only; the endpoint 404s in prod AND the repo refuses when
  // ENVIRONMENT=production.
  wipeObservations: (accountLogin: string) =>
    api.post<{
      wiped: boolean;
      total: number;
      counts: Record<string, number>;
      // S3 blame + clone cache eviction result (best-effort; cache_error set if the
      // DB wipe succeeded but the cache couldn't be evicted, e.g. IAM not applied).
      cache?: { bucket: string; blame_deleted: number; clone_deleted: number };
      cache_error?: string;
    }>(
      withAccount("/dev/observations/wipe", requireAccount(accountLogin, "devApi.wipeObservations")),
      {},
    ),
};
